import React from 'react';
import PropTypes from 'prop-types';
import loadable from '@loadable/component';
import * as Sentry from '@sentry/react';

import AttendeeTicketNotification from '@eb/attendee-ticket-notification';
import { constants as destinationConstants } from '@eb/destination-utils';
import { trackAnalyticsEvent } from '@eb/site-analytics';
import { isMediumNarrowDown } from '@eb/eds-utils';
import { Layout } from '@eb/eds-layout';

import EnhancedHomeConsumerHeader from '../components/EnhancedHomeConsumerHeader';
import {
    BUCKETS_PROPTYPES,
    FEATURE_FLAGS_PROPTYPES,
    ORGANIZER_ARTICLE_BUCKET_PROPTYPE,
    FLAT_BUCKET_PROPTYPE,
} from '../constants/propTypes';
import {
    GA_HOME_FEED_CATEGORY,
    GA_VIEW_HOMEPAGE_FEED,
    GA_VIEW_LOGGEDOUT_HOMEPAGE_FEED,
    GA_HOME_CATEGORY,
    GA_EXPERIMENT_EXPOSURE,
} from '../constants/analytics';
import { storeHeroImageIndex } from '../utils/localStorage';

const ConnectedBasePage = loadable(() => import('./ConnectedBasePage'));
const ConnectedHeader = loadable(() => import('../containers/ConnectedHeader'));
const BrowseContainer = loadable(() => import('../containers/BrowseContainer'));
// const CoronaVirusBanner = loadable(() =>
//     import('../components/Banners/CoronaVirusBanner'),
// );
const USBanner = loadable(() => import('../components/Banners/USBanner'));

const { PLACE_OBJECT_PROPTYPE } = destinationConstants;

// This is mainly here because on the feed we want to track extra stuff
// on componentDidMount in addition to the pageview we already are tracking,
// and we want to keep it consistent where the category will be different depending
// on logged-in or logged-out status.
export const getFeedTrackingProperties = (
    isAuthenticated,
    userId,
    { experimentId = null, experimentId3 = null },
) => {
    // Non-auth'd users, Category is "home"
    let category = GA_HOME_CATEGORY;

    let action = GA_VIEW_LOGGEDOUT_HOMEPAGE_FEED;

    let dimensions = {};

    // Auth'd users, Category is "home-feed"
    if (isAuthenticated) {
        category = GA_HOME_FEED_CATEGORY;
        action = GA_VIEW_HOMEPAGE_FEED;
        dimensions = { userId };
    }

    if (experimentId) {
        dimensions = {
            ...dimensions,
            experimentId,
        };

        action = GA_EXPERIMENT_EXPOSURE;
    }

    if (experimentId3) {
        dimensions = {
            ...dimensions,
            experimentId3,
        };
    }

    return {
        category,
        action,
        nonInteraction: true,
        dimensions,
    };
};

const applyIEFixes = () => {
    /**
     * IE is returning errors because SVG elements are not correctly mapped when parsing the docs.
     * This post gives a solution to this: https://github.com/Pomax/react-onclickoutside#ie-does-not-support-classlist-for-svg-elements
     */
    if (!('classList' in SVGElement.prototype)) {
        Object.defineProperty(SVGElement.prototype, 'classList', {
            get() {
                return {
                    contains: (className) =>
                        this.className.baseVal.split(' ').indexOf(className) !==
                        -1,
                };
            },
        });
    }
};

// Deal with the messed up Varnish urls (/esi_cahe/?...) which break the signin link
const fixLoginUrl = (loginUrl) => {
    const splitUrl = loginUrl.split('/');
    const urlBeginning = splitUrl.slice(0, -1);

    return urlBeginning.concat('?referrer=%2F').join('/');
};

export default class FeedPage extends React.Component {
    static propTypes = {
        // feed props
        buckets: BUCKETS_PROPTYPES,
        featureFlags: FEATURE_FLAGS_PROPTYPES,
        placeId: PropTypes.string,
        latitude: PropTypes.number,
        longitude: PropTypes.number,
        currentPlace: PropTypes.string,
        currentPlaceParent: PropTypes.string,
        locations: PropTypes.shape({
            places: PropTypes.arrayOf(PLACE_OBJECT_PROPTYPE),
        }),
        placeType: PropTypes.string,
        slug: PropTypes.string,
        fiveToFollow: PropTypes.arrayOf(ORGANIZER_ARTICLE_BUCKET_PROPTYPE),
        flatBucket: FLAT_BUCKET_PROPTYPE,
        isAuthenticated: PropTypes.bool,
        userId: PropTypes.string,
        handleRestOfContext: PropTypes.func,
        originalContext: PropTypes.object,
        history: PropTypes.object,
        env: PropTypes.object,
    };

    static defaultProps = {
        handleRestOfContext: () => null,
    };

    state = {
        isInBrowser: false,
        // Whether user is enrolled in the "search bar on homepage" experiment with Optimize
        // we don't want to default to variant A because we want to show something different than
        // A while we wait for Optimize to return
        isInSearchBarExperimentVariant: null,
        isMobile: false,
    };

    componentDidMount() {
        const { handleRestOfContext, originalContext } = this.props;

        Sentry.setTag('sub_app', 'home');

        applyIEFixes();

        handleRestOfContext(originalContext);

        // Updates the last hero image seen to get a new one.
        storeHeroImageIndex();

        this.setState({
            isInBrowser: true,
            isMobile: isMediumNarrowDown(),
        });
    }

    render() {
        // During the server render, because of Varnish, we want to render the page
        // as a logged-out user. Once we are in the browser, we have the props for the user
        // and we want to update the page. Since most of our components do not keep this in mind,
        // we basically have to do it with componentDidMount or else we get weird markup mismatch issues.
        // Once we're in the browser, we'll go to use the normal props.
        // See also the ConsumerHeader/index.js
        let overrideProps = this.state.isInBrowser
            ? {}
            : {
                  env: {
                      ...this.props.env,
                      loginUrl: fixLoginUrl(this.props.env.loginUrl),
                      showCookieHeader: false,
                  },
                  footer: <div />,
                  user: {
                      isAuthenticated: false,
                      publicId: null,
                      canCreateEvents: false,
                  },
              };

        return (
            <ConnectedBasePage
                {...this.props}
                {...overrideProps}
                isHomePage={true}
                structureHeader={EnhancedHomeConsumerHeader}
            >
                <Layout hasHorizontalGutters={true} maxWidth="large">
                    <AttendeeTicketNotification
                        shouldAnimateEntry={true}
                        isAuthenticated={this.props.isAuthenticated}
                        containerStyles={
                            this.state.isMobile
                                ? { margin: '0 auto', top: '120px' }
                                : { right: '32px', top: '120px' }
                        }
                        onLinkClick={(eid, hasDigitalContent) =>
                            trackAnalyticsEvent({
                                category: GA_HOME_FEED_CATEGORY,
                                label: eid,
                                action: hasDigitalContent
                                    ? 'UpcomingOnlineEventViewed'
                                    : 'UpcomingTicketViewed',
                            })
                        }
                        onDismiss={(timeUntilEvent, hasDigitalContent) =>
                            trackAnalyticsEvent({
                                category: GA_HOME_FEED_CATEGORY,
                                label: timeUntilEvent,
                                action: hasDigitalContent
                                    ? 'UpcomingOnlineEventDismissed'
                                    : 'UpcomingTicketDismissed',
                            })
                        }
                    />
                    <>
                        <div className="eds-l-pad-top-2">
                            <USBanner />
                        </div>
                        <ConnectedHeader history={this.props.history} />
                    </>
                    <BrowseContainer />
                </Layout>
            </ConnectedBasePage>
        );
    }
}
